import { useEffect, useReducer } from "react";

import { isNil } from "../../utils/fp";
import { useTicketVersionStatusMutation } from "../../utils/queries/projects";

type DocumentStatusUpdaterProps = {
  ticketId: string;
  revisionId: string;
  statuses: {
    isPending: boolean;
    isAIReady: boolean;
  };
  isDone: boolean;
  onReady(): void;
  onAIReady(): void;
  onFail(): void;
  needsRun: boolean;
};

const INTERVAL_DURATION = 3000;

let secondRun = true;

export default function DocumentStatusUpdater(
  props: DocumentStatusUpdaterProps,
) {
  const {
    ticketId,
    revisionId,
    onReady,
    onAIReady,
    onFail,
    statuses,
    isDone,
    needsRun,
  } = props;

  const ticketVersionStatusMutation = useTicketVersionStatusMutation(ticketId);

  const MAX_CHECKS = 40;
  const [currentCheckCount, incrementCheckCount] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    if (needsRun && !isDone && !secondRun) {
      secondRun = true;
      return onAIReady();
    }
  }, [needsRun, isDone]);

  useEffect(() => {
    if (isDone) return;

    // When uploading a version that results in doc mode, we will have to wait
    // for some amount of processing to be done on the API. Because of this, we
    // will poll the status at some interval (3 seconds currently).
    if (!statuses.isPending && statuses.isAIReady) {
      return;
    }

    if (currentCheckCount >= MAX_CHECKS) {
      return onFail();
    }

    const interval = setInterval(() => {
      console.warn("Getting version status", ticketId, revisionId);
      console.table(statuses);

      incrementCheckCount();

      ticketVersionStatusMutation.mutate(revisionId, {
        onSuccess(data) {
          if (isNil(data)) return;

          // We can load the asset image URLs
          if (!data.isPending && statuses.isPending) {
            onReady();
          }

          // We can run an AI review
          if (data.isAIReady && !statuses.isAIReady) {
            onAIReady();
          }
        },
      });
    }, INTERVAL_DURATION);

    return () => {
      clearInterval(interval);
    };
  }, [revisionId, statuses.isAIReady, statuses.isPending, isDone]);

  return null;
}
